"use client";

import Link from "next/link";
import { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { Loader2, LogOut, Plus, Power, Trash2, Terminal, ScrollText } from "lucide-react";
import { ParentsSection } from "@/components/parents-section";
import { PrefsControls } from "@/components/prefs-controls";
import { usePrefs } from "@/components/prefs-provider";

type Child = {
  id: string;
  name: string;
  sessionId: string;
  phone: string | null;
  connected: boolean;
  running: boolean;
};

type User = {
  id: string;
  name: string;
  email: string;
  plan: { planName: string | null; active: boolean; expiredAt: string | null } | null;
  children: Child[];
  reportsSettings: string;
};

type ParsedUpdate = {
  seq: number;
  at: string;
  kind: string;
  text: string;
  mediaType?: string;
  mime?: string;
  data?: string;
  fileName?: string;
};

function mediaSrc(u: ParsedUpdate) {
  if (!u.data) return "";
  return `data:${u.mime || "application/octet-stream"};base64,${u.data}`;
}

function ParsedMedia({ u }: { u: ParsedUpdate }) {
  if (!u.data || !u.mediaType) return null;
  const src = mediaSrc(u);
  const box = "mt-3 max-h-80 max-w-full rounded-lg";
  switch (u.mediaType) {
    case "image":
    case "sticker":
      return <img src={src} alt={u.mediaType} className={box} />;
    case "gif":
      return <video src={src} autoPlay loop muted playsInline className={box} />;
    case "video":
      return <video src={src} controls playsInline className={box} />;
    case "audio":
    case "voice":
      return <audio src={src} controls className="mt-3 w-full" />;
    default:
      return (
        <a href={src} download={u.fileName || u.mediaType} className="mt-3 inline-block text-sm font-semibold text-emerald-300 underline">
          Download {u.fileName || u.mediaType}
        </a>
      );
  }
}

type QrState = {
  qrDataUrl: string | null;
  connected: boolean;
  running: boolean;
  pairingCode?: string | null;
  phone?: string | null;
  mock?: boolean;
};

export default function DashboardPage() {
  const router = useRouter();
  const { t, tf, locale } = usePrefs();
  const settingsHydrated = useRef(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [childName, setChildName] = useState("");
  const [error, setError] = useState("");
  const [activeChild, setActiveChild] = useState<string | null>(null);
  const [qr, setQr] = useState<QrState | null>(null);
  const [pairPhone, setPairPhone] = useState("");
  const [updatesChildId, setUpdatesChildId] = useState<string | null>(null);
  const [updates, setUpdates] = useState<{ seq: number; at: string; type: string; data: unknown }[]>([]);
  const [updatesStatus, setUpdatesStatus] = useState("connecting…");
  const [parsedChildId, setParsedChildId] = useState<string | null>(null);
  const [parsed, setParsed] = useState<ParsedUpdate[]>([]);
  const [parsedStatus, setParsedStatus] = useState("connecting…");
  const [settings, setSettings] = useState({
    ignoreGroupRemovals: false,
    ignoreChildSelfMessages: true,
    ignoreBlockReports: false,
    hideInappropriateContent: false,
  });
  const [verifiedParents, setVerifiedParents] = useState(0);
  const parentsReady = verifiedParents > 0;

  const refresh = useCallback(async () => {
    const res = await fetch("/api/auth/me");
    const data = await res.json().catch(() => ({ user: null }));
    if (!res.ok || !data.user) {
      router.push("/login");
      return;
    }
    setUser(data.user);
    try {
      const parsed = JSON.parse(data.user.reportsSettings || "{}") as Partial<typeof settings>;
      setSettings((prev) => ({
        ...prev,
        ignoreGroupRemovals: parsed.ignoreGroupRemovals ?? prev.ignoreGroupRemovals,
        ignoreChildSelfMessages: parsed.ignoreChildSelfMessages ?? prev.ignoreChildSelfMessages,
        ignoreBlockReports: parsed.ignoreBlockReports ?? prev.ignoreBlockReports,
        hideInappropriateContent: parsed.hideInappropriateContent ?? prev.hideInappropriateContent,
      }));
    } catch {
      /* ignore */
    }
    setLoading(false);
  }, [router]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!user) return;
    if (!settingsHydrated.current) {
      settingsHydrated.current = true;
      return;
    }
    const timer = setTimeout(() => {
      fetch("/api/settings", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reportsSettings: { ...settings, alertLanguage: locale } }),
      }).catch(() => undefined);
    }, 250);
    return () => clearTimeout(timer);
  }, [settings, locale, user]);

  useEffect(() => {
    if (!activeChild) return;
    let alive = true;
    const tick = async () => {
      const res = await fetch(`/api/children/${activeChild}/qr`);
      if (!alive) return;
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || t.dash.qrFail);
        return;
      }
      setError("");
      setQr(data);
      if (data.connected) {
        setActiveChild(null);
        setQr(null);
        refresh();
      }
    };
    tick();
    const id = setInterval(tick, 2000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [activeChild, refresh]);

  useEffect(() => {
    if (!user) return;
    const needsPhone = user.children.filter((c) => c.connected && !c.phone);
    if (needsPhone.length === 0) return;
    let alive = true;
    let tries = 0;
    const run = async () => {
      for (const c of needsPhone) {
        await fetch(`/api/children/${c.id}/status`).catch(() => undefined);
      }
      if (!alive) return;
      await refresh();
      tries += 1;
      // stop hammering after a couple attempts
      if (tries < 2) {
        setTimeout(() => {
          if (alive) run();
        }, 1500);
      }
    };
    run();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.children.map((c) => `${c.id}:${c.connected}:${c.phone}`).join("|")]);

  async function logout() {
    await fetch("/api/auth/logout", { method: "POST" });
    router.push("/");
  }

  async function addChild(e: FormEvent) {
    e.preventDefault();
    if (!user?.plan?.active) {
      setError(t.dash.needPlanAdd);
      return;
    }
    setError("");
    const res = await fetch("/api/children", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: childName }),
    });
    const data = await res.json();
    if (!res.ok) {
      setError(data.error || t.dash.addFail);
      return;
    }
    setChildName("");
    await refresh();
  }

  async function startChild(id: string) {
    setError("");
    const res = await fetch(`/api/children/${id}/start`, { method: "POST" });
    const data = await res.json();
    if (!res.ok) {
      setError(data.error || t.dash.startFail);
      return;
    }
    await refresh();
    const linked = data.child?.connected;
    if (!linked) {
      setQr(null);
      setActiveChild(id);
    } else {
      setActiveChild(null);
      setQr(null);
    }
  }

  async function stopChild(id: string) {
    await fetch(`/api/children/${id}/stop`, { method: "POST" });
    if (activeChild === id) setActiveChild(null);
    await refresh();
  }

  async function unlinkChild(id: string) {
    if (!confirm(t.dash.unlinkConfirm)) return;
    setError("");
    const res = await fetch(`/api/children/${id}/logout`, { method: "POST" });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      setError(data.error || t.dash.unlinkFail);
      return;
    }
    setQr(null);
    setActiveChild(null);
    await refresh();
  }

  async function deleteChild(id: string) {
    const child = user?.children.find((c) => c.id === id);
    const msg = child?.connected ? t.dash.deleteLinked : t.dash.deletePlain;
    if (!confirm(msg)) return;
    setError("");
    const res = await fetch(`/api/children/${id}`, { method: "DELETE" });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      setError(data.error || t.dash.deleteFail);
      return;
    }
    if (activeChild === id) setActiveChild(null);
    await refresh();
  }

  async function pairCode() {
    if (!activeChild) return;
    const res = await fetch(`/api/children/${activeChild}/qr`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "pair", phone: pairPhone }),
    });
    const data = await res.json();
    if (!res.ok) {
      setError(data.error || t.dash.pairFail);
      return;
    }
    setQr((q) => (q ? { ...q, pairingCode: data.pairingCode } : q));
  }

  async function mockScan() {
    if (!activeChild) return;
    await fetch(`/api/children/${activeChild}/qr`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "mock-connect" }),
    });
    await refresh();
  }

  useEffect(() => {
    if (!updatesChildId) return;
    setUpdates([]);
    setUpdatesStatus("connecting WhatsApp websocket…");
    const es = new EventSource(`/api/children/${updatesChildId}/events/stream`);
    es.onopen = () => setUpdatesStatus("live — waiting for WhatsMeow events");
    es.onmessage = (e) => {
      try {
        const evt = JSON.parse(e.data) as { seq: number; at: string; type: string; data: unknown };
        if (evt.type === "chatisafe.ListenerReady") {
          setUpdatesStatus("socket connected — send a message now");
        }
        setUpdates((prev) => [...prev, evt].slice(-500));
      } catch {
        /* ignore keepalive */
      }
    };
    es.onerror = () => {
      setUpdatesStatus("stream error — check WhatsMeow is running and protection is on");
    };
    return () => es.close();
  }, [updatesChildId]);

  function openUpdates(childId: string) {
    setUpdatesChildId(childId);
  }

  async function clearUpdates() {
    if (!updatesChildId) return;
    await fetch(`/api/children/${updatesChildId}/events`, { method: "DELETE" });
    setUpdates((prev) => prev.filter((u) => u.type === "chatisafe.ListenerReady"));
  }

  useEffect(() => {
    if (!parsedChildId) return;
    setParsed([]);
    setParsedStatus("connecting WhatsApp websocket…");
    const es = new EventSource(`/api/children/${parsedChildId}/parsed/stream`);
    es.onopen = () => setParsedStatus("live — waiting for parsed updates");
    es.onmessage = (e) => {
      try {
        const evt = JSON.parse(e.data) as ParsedUpdate;
        if (evt.kind === "ready") {
          setParsedStatus("socket connected — trigger an event on the phone");
          return;
        }
        setParsed((prev) => [...prev, evt].slice(-500));
      } catch {
        /* ignore keepalive */
      }
    };
    es.onerror = () => {
      setParsedStatus("stream error — check WhatsMeow is running and protection is on");
    };
    return () => es.close();
  }, [parsedChildId]);

  async function clearParsed() {
    if (!parsedChildId) return;
    await fetch(`/api/children/${parsedChildId}/parsed`, { method: "DELETE" });
    setParsed([]);
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[var(--teal)]" />
      </div>
    );
  }

  if (!user) return null;

  const planOk = !!user.plan?.active;

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <header className="border-b border-[var(--line)] bg-[var(--foam)]/80 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-3 px-4 sm:px-6">
          <Link href="/" className="font-display shrink-0 text-xl font-bold">
            Chati<span className="text-[var(--teal)]">Safe</span>
          </Link>
          <div className="flex items-center gap-2 text-sm sm:gap-3">
            <PrefsControls compact />
            <Link href="/plans" className="font-medium text-[var(--ink-soft)] hover:text-[var(--ink)]">
              {t.nav.plans}
            </Link>
            <button onClick={logout} className="inline-flex items-center gap-1.5 font-semibold text-[var(--ink-soft)]">
              <LogOut className="h-4 w-4" /> {t.nav.logout}
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <p className="text-sm text-[var(--ink-soft)]">{tf(t.dash.signedIn, { email: user.email })}</p>
            <h1 className="font-display mt-1 text-3xl font-bold">
              {tf(t.dash.hey, { name: user.name.split(" ")[0] })}
            </h1>
          </div>
          <div
            className={`rounded-xl px-4 py-2 text-sm font-semibold ${
              planOk
                ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-400/15 dark:text-emerald-200"
                : "bg-amber-100 text-amber-900 dark:bg-amber-400/15 dark:text-amber-200"
            }`}
          >
            {planOk
              ? tf(t.dash.plan, { name: t.dash.planNames[user.plan?.planName || ""] || user.plan?.planName || "" })
              : t.dash.paused}
          </div>
        </div>

        {error && <p className="mt-4 text-sm text-[var(--alert)]">{error}</p>}

        <section className="mt-10">
          <div className="flex items-center justify-between gap-3">
            <h2 className="font-display text-2xl font-bold">{t.dash.children}</h2>
          </div>
          <form onSubmit={addChild} className="mt-4 flex flex-col gap-2 sm:flex-row">
            <input
              value={childName}
              onChange={(e) => setChildName(e.target.value)}
              placeholder={t.dash.childPlaceholder}
              className="input"
              required
              disabled={!planOk}
            />
            <button className="btn-primary shrink-0" type="submit" disabled={!planOk}>
              <Plus className="h-4 w-4" /> {t.dash.addChild}
            </button>
          </form>
          {!planOk && (
            <p className="mt-2 text-sm text-amber-800 dark:text-amber-200">
              {t.dash.needPlan}{" "}
              <Link href="/plans" className="font-semibold underline">
                {t.dash.viewPlans}
              </Link>
            </p>
          )}

          <div className="mt-6 grid gap-4">
            {user.children.length === 0 && (
              <p className="text-[var(--ink-soft)]">{t.dash.noChildren}</p>
            )}
            {user.children.map((c) => {
              const pairing = !c.connected && c.running;
              const protectedOn = c.connected && c.running;

              return (
                <div key={c.id} className="rounded-2xl border border-[var(--line)] bg-[var(--foam)] p-5">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="font-display text-xl font-bold">{c.name}</p>
                      <p className="mt-1 text-sm text-[var(--ink-soft)]">
                        {c.connected ? (
                          <>
                            <span className={protectedOn ? "text-emerald-700 dark:text-emerald-300" : "text-amber-700 dark:text-amber-300"}>
                              {protectedOn ? t.dash.on : t.dash.linkedPaused}
                            </span>
                            {c.phone ? (
                              <span className="mt-2 block font-display text-lg font-bold tracking-wide text-[var(--teal)]">
                                {c.phone}
                              </span>
                            ) : (
                              <span className="mt-2 block text-sm text-amber-800 dark:text-amber-200">
                                {t.dash.noNumber}
                              </span>
                            )}
                          </>
                        ) : pairing ? (
                          <span className="text-amber-700 dark:text-amber-300">{t.dash.waitingScan}</span>
                        ) : (
                          <span className="text-amber-700 dark:text-amber-300">{t.dash.notLinked}</span>
                        )}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {c.connected ? (
                        <>
                          {protectedOn ? (
                            <button onClick={() => stopChild(c.id)} className="btn-ghost !py-2 text-sm">
                              {t.dash.pause}
                            </button>
                          ) : (
                            <button
                              onClick={() => startChild(c.id)}
                              className="btn-primary !py-2 text-sm"
                              disabled={!planOk}
                            >
                              <Power className="h-4 w-4" /> {t.dash.start}
                            </button>
                          )}
                          <button onClick={() => unlinkChild(c.id)} className="btn-ghost !py-2 text-sm">
                            {t.dash.unlink}
                          </button>
                          <button onClick={() => openUpdates(c.id)} className="btn-ghost !py-2 text-sm">
                            <Terminal className="h-4 w-4" /> SHOW UPDATES
                          </button>
                          <button onClick={() => setParsedChildId(c.id)} className="btn-ghost !py-2 text-sm">
                            <ScrollText className="h-4 w-4" /> SHOW PARSED UPDATES
                          </button>
                        </>
                      ) : pairing ? (
                        <button onClick={() => stopChild(c.id)} className="btn-ghost !py-2 text-sm">
                          {t.dash.cancelPair}
                        </button>
                      ) : (
                        <button
                          onClick={() => startChild(c.id)}
                          className="btn-primary !py-2 text-sm"
                          disabled={!planOk}
                        >
                          <Power className="h-4 w-4" /> {t.dash.linkWa}
                        </button>
                      )}
                      <button onClick={() => deleteChild(c.id)} className="btn-ghost !py-2 text-sm text-[var(--alert)]">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {pairing && activeChild === c.id && (
                    <div className="mt-6 grid gap-6 border-t border-[var(--line)] pt-6 lg:grid-cols-2">
                      <div className="flex flex-col items-center justify-center rounded-xl bg-[var(--sand)] p-6">
                        {qr?.mock && (
                          <p className="mb-3 rounded-lg bg-amber-100 px-3 py-2 text-center text-xs font-semibold text-amber-900 dark:bg-amber-400/15 dark:text-amber-200">
                            {t.dash.mockQr}
                          </p>
                        )}
                        {qr?.qrDataUrl ? (
                          <>
                            {/* eslint-disable-next-line @next/next/no-img-element */}
                            <img src={qr.qrDataUrl} alt="WhatsApp QR" className="h-64 w-64 rounded-lg bg-white p-2" />
                            <p className="mt-3 text-center text-sm text-[var(--ink-soft)]">
                              {t.dash.qrHint}
                            </p>
                          </>
                        ) : (
                          <div className="flex flex-col items-center gap-2">
                            <Loader2 className="h-6 w-6 animate-spin text-[var(--teal)]" />
                            <p className="text-xs text-[var(--ink-soft)]">{t.dash.waitingQr}</p>
                          </div>
                        )}
                        {qr?.mock && (
                          <button onClick={mockScan} className="btn-ghost mt-4 !py-2 text-xs">
                            {t.dash.mockScan}
                          </button>
                        )}
                      </div>
                      <div>
                        <p className="font-semibold">{t.dash.orCode}</p>
                        <div className="mt-3 flex gap-2">
                          <input
                            className="input"
                            placeholder={t.dash.phonePh}
                            value={pairPhone}
                            onChange={(e) => setPairPhone(e.target.value)}
                          />
                          <button onClick={pairCode} className="btn-primary shrink-0 !py-2 text-sm" type="button">
                            {t.dash.getCode}
                          </button>
                        </div>
                        {qr?.pairingCode && (
                          <p className="mt-4 font-display text-3xl font-bold tracking-[0.3em] text-[var(--teal)]">
                            {qr.pairingCode}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        <section className="mt-12 rounded-2xl border border-[var(--line)] bg-[var(--foam)] p-5">
          <h2 className="font-display text-2xl font-bold">{t.dash.settings}</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {(
              [
                ["ignoreGroupRemovals", t.dash.ignoreGroup],
                ["ignoreChildSelfMessages", t.dash.ignoreSelf],
                ["ignoreBlockReports", t.dash.ignoreBlock],
                ["hideInappropriateContent", t.dash.hideBody],
              ] as const
            ).map(([key, label]) => (
              <label key={key} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={settings[key]}
                  onChange={(e) => setSettings((s) => ({ ...s, [key]: e.target.checked }))}
                />
                {label}
              </label>
            ))}
          </div>
        </section>
      </main>

      {parsedChildId && (
        <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/50 p-3 sm:items-center">
          <div className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-[#0b1210] text-[#d7e6df] shadow-2xl">
            <div className="flex items-center justify-between gap-3 border-b border-white/10 px-4 py-3">
              <div>
                <p className="text-xs uppercase tracking-[0.16em] text-emerald-400/80">PARSED</p>
                <p className="font-display text-lg font-bold text-white">SHOW PARSED UPDATES</p>
                <p className="text-xs text-emerald-300/80">{parsedStatus}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={clearParsed} className="rounded-lg border border-white/15 px-3 py-1.5 text-xs font-semibold">
                  Clear
                </button>
                <button
                  onClick={() => setParsedChildId(null)}
                  className="rounded-lg bg-white/10 px-3 py-1.5 text-xs font-semibold text-white"
                >
                  Close
                </button>
              </div>
            </div>
            <div className="flex-1 space-y-3 overflow-y-auto p-4 text-sm leading-relaxed">
              {parsed.length === 0 && (
                <p className="p-4 text-white/50">Listening… send a message, join/leave a group, or block someone.</p>
              )}
              {parsed.map((u) => (
                <div key={u.seq} className="rounded-lg border border-white/10 bg-black/40 p-3 text-[#d7e6df]">
                  <p className="mb-2 text-[11px] text-emerald-300/70">
                    {new Date(u.at).toLocaleString()}
                  </p>
                  <p className="whitespace-pre-wrap break-words">{u.text}</p>
                  <ParsedMedia u={u} />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {updatesChildId && (
        <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/50 p-3 sm:items-center">
          <div className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-[#0b1210] text-[#d7e6df] shadow-2xl">
            <div className="flex items-center justify-between gap-3 border-b border-white/10 px-4 py-3">
              <div>
                <p className="text-xs uppercase tracking-[0.16em] text-emerald-400/80">TEMP DEBUG</p>
                <p className="font-display text-lg font-bold text-white">Raw WhatsMeow updates</p>
                <p className="text-xs text-emerald-300/80">{updatesStatus}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={clearUpdates} className="rounded-lg border border-white/15 px-3 py-1.5 text-xs font-semibold">
                  Clear
                </button>
                <button
                  onClick={() => setUpdatesChildId(null)}
                  className="rounded-lg bg-white/10 px-3 py-1.5 text-xs font-semibold text-white"
                >
                  Close
                </button>
              </div>
            </div>
            <div className="flex-1 space-y-2 overflow-y-auto p-3 font-mono text-[11px] leading-relaxed">
              {updates.length === 0 && (
                <p className="p-4 text-sm text-white/50">Listening… send a message / leave a group / etc.</p>
              )}
              {updates.map((u) => (
                <pre
                  key={u.seq}
                  className="overflow-x-auto rounded-lg border border-white/10 bg-black/40 p-3 whitespace-pre-wrap break-all text-[#b7f0d2]"
                >
                  {JSON.stringify(u, null, 2)}
                </pre>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
